import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ListChecks, Pencil, Loader2, CheckCircle2, Ban, AlertTriangle, AlertCircle } from "lucide-react";
import { fetchAllBookings, userCancelBooking } from "@/lib/api";
import { formatDMY } from "@/lib/date";
import { UserUpdateDialog } from "@/components/UserUpdateDialog";
import { CancelBookingDialog } from "@/components/CancelBookingDialog";
import { OtpDialog } from "@/components/OtpDialog";
import { BookingsPagination } from "@/components/BookingsPagination";
import { MobileBookingCard } from "@/components/MobileBookingCard";

const PAGE_SIZE = 20;

const fmtDuration = (min) => {
  if (min == null || min <= 0) return "—";
  const h = Math.floor(min / 60);
  const m = min % 60;
  return `${h > 0 ? `${h}h ` : ""}${m}m`;
};
const fmtTimeFromIso = (iso) => (iso ? iso.slice(11, 16) : "—");

const statusStyles = {
  Upcoming: "bg-slate-100 text-slate-600 ring-slate-200",
  "In Use": "bg-blue-50 text-blue-700 ring-blue-200",
  Overdue: "bg-red-50 text-red-600 ring-red-200",
  Returned: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  Cancelled: "bg-rose-50 text-rose-600 ring-rose-200",
};

export const AllBookingsTab = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [cancelling, setCancelling] = useState(null);
  const [otpFor, setOtpFor] = useState(null);
  const [page, setPage] = useState(1);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["all-bookings", page],
    queryFn: () => fetchAllBookings({ page, page_size: PAGE_SIZE }),
    keepPreviousData: true,
  });

  const bookings = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.total_pages ?? 1;

  // If records change and the current page becomes invalid, snap to the nearest valid page.
  useEffect(() => {
    if (data && data.page !== page) setPage(data.page);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaved = () => {
    setEditing(null);
    queryClient.invalidateQueries({ queryKey: ["all-bookings"] });
    queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  const handleCancelConfirm = async ({ reason }) => {
    await userCancelBooking(cancelling.booking.booking_ref, { verify_token: cancelling.token, reason });
    toast.success(`Booking ${cancelling.booking.booking_ref} cancelled`);
    setCancelling(null);
    queryClient.invalidateQueries({ queryKey: ["all-bookings"] });
    queryClient.invalidateQueries({ queryKey: ["availability"] });
  };

  const handleVerified = (token) => {
    const { booking, action } = otpFor;
    setOtpFor(null);
    if (action === "update") setEditing({ booking, token });
    else setCancelling({ booking, token });
  };

  const renderActions = (b, p = "") =>
    b.status === "Cancelled" ? (
      <span className="text-xs font-semibold text-rose-500">Cancelled</span>
    ) : !b.is_owner ? (
      <span className="text-xs text-[#94a3b8]" data-testid={`${p}all-notowner-${b.booking_ref}`} title="You can only manage your own bookings">{p ? "Only the booking owner can manage this" : "—"}</span>
    ) : (
      <div className="flex items-center gap-2">
        {b.actual_return_datetime != null && b.current_battery_percentage != null ? (
          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600" data-testid={`${p}all-done-${b.booking_ref}`}>
            <CheckCircle2 className="h-3.5 w-3.5" /> Completed
          </span>
        ) : (
          <button onClick={() => setOtpFor({ booking: b, action: "update" })} className="flex items-center gap-1.5 rounded-lg bg-[#5C2D91] px-3 py-1.5 text-xs font-semibold text-white transition-all hover:bg-[#4A2475]" data-testid={`${p}all-update-${b.booking_ref}`}>
            <Pencil className="h-3.5 w-3.5" /> Update
          </button>
        )}
        <button onClick={() => setOtpFor({ booking: b, action: "cancel" })} disabled={b.status === "Returned"} className="flex items-center gap-1.5 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs font-semibold text-red-600 transition-all hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-red-50" data-testid={`${p}all-cancel-${b.booking_ref}`}>
          <Ban className="h-3.5 w-3.5" /> Cancel
        </button>
        {b.potential_delay && (
          <span title="Potential Delay" className="ml-auto" data-testid={`${p}all-delay-icon-${b.booking_ref}`}>
            <AlertTriangle className="h-4 w-4 shrink-0 text-amber-500" />
          </span>
        )}
      </div>
    );

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6"
      data-testid="all-bookings-section"
    >
      <div className="mb-4 flex items-center gap-2">
        <ListChecks className="h-5 w-5 text-[#5C2D91]" />
        <h2 className="font-heading text-lg font-semibold text-[#0F172A]">All Bookings</h2>
      </div>
      <p className="mb-4 text-sm text-[#64748B]">
        View all fleet bookings. You can update the Actual Return Time and Battery % only for bookings made with your email.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-[#64748B]" data-testid="all-bookings-loading">
          <Loader2 className="h-4 w-4 animate-spin text-[#5C2D91]" /> Loading bookings…
        </div>
      ) : isError ? (
        <div className="flex flex-col items-center gap-3 py-10 text-center" data-testid="all-bookings-error">
          <AlertCircle className="h-8 w-8 text-red-500" />
          <p className="text-sm text-[#64748B]">Could not load bookings. Please try again.</p>
          <button
            onClick={() => refetch()}
            className="rounded-lg bg-[#5C2D91] px-4 py-2 text-sm font-semibold text-white transition-all hover:bg-[#4A2475]"
            data-testid="all-bookings-retry"
          >
            Retry
          </button>
        </div>
      ) : bookings.length === 0 ? (
        <p className="py-8 text-center text-sm text-[#64748B]" data-testid="all-bookings-empty">No bookings yet.</p>
      ) : (
        <>
          <div className="space-y-3 md:hidden" data-testid="all-bookings-cards">
            {bookings.map((b) => (
              <MobileBookingCard key={b.booking_ref} b={b} statusCls={statusStyles[b.status]} fmtTime={fmtTimeFromIso} fmtDuration={fmtDuration} actions={renderActions(b, "m-")} testid={`all-card-${b.booking_ref}`} />
            ))}
          </div>
          <div className="hidden overflow-x-auto md:block">
            <table className="w-full min-w-[1100px] text-left text-sm" data-testid="all-bookings-table">
              <thead>
                <tr className="border-b border-slate-200 text-xs uppercase tracking-wide text-[#64748B]">
                  <th className="py-3 pr-4">Ref</th>
                  <th className="py-3 pr-4">User</th>
                  <th className="py-3 pr-4">Vehicle</th>
                  <th className="py-3 pr-4">Date</th>
                  <th className="py-3 pr-4">Start</th>
                  <th className="py-3 pr-4">Est. Return</th>
                  <th className="py-3 pr-4">Actual Return</th>
                  <th className="py-3 pr-4">Usage</th>
                  <th className="py-3 pr-4">Overdue</th>
                  <th className="py-3 pr-4">Battery</th>
                  <th className="py-3 pr-4">Status</th>
                  <th className="py-3 pr-4">Action</th>
                </tr>
              </thead>
              <tbody>
                {bookings.map((b) => (
                  <tr key={b.booking_ref} className="border-b border-slate-100 last:border-0" data-testid={`all-row-${b.booking_ref}`}>
                    <td className="py-3 pr-4 font-mono font-semibold text-[#5C2D91]">{b.booking_ref}</td>
                    <td className="py-3 pr-4">
                      <div className="font-medium text-[#0F172A]">{b.employee_name}</div>
                    </td>
                    <td className="py-3 pr-4 text-[#0F172A]">{b.vehicle}</td>
                    <td className="py-3 pr-4 text-[#0F172A]">{formatDMY(b.booking_date)}</td>
                    <td className="py-3 pr-4 text-[#0F172A]">{b.start_time}</td>
                    <td className="py-3 pr-4 text-[#0F172A]">{b.return_time}</td>
                    <td className="py-3 pr-4 text-[#0F172A]">{fmtTimeFromIso(b.actual_return_datetime)}</td>
                    <td className="py-3 pr-4 text-[#0F172A]">{fmtDuration(b.actual_duration_minutes)}</td>
                    <td className="py-3 pr-4">
                      <span className={b.overdue_minutes > 0 ? "font-semibold text-red-600" : "text-[#64748B]"}>{fmtDuration(b.overdue_minutes)}</span>
                    </td>
                    <td className="py-3 pr-4 text-[#0F172A]">{b.current_battery_percentage != null ? `${b.current_battery_percentage}%` : "—"}</td>
                    <td className="py-3 pr-4">
                      <span className={`inline-block rounded-full px-2.5 py-1 text-xs font-semibold ring-1 ${statusStyles[b.status]}`}>{b.status}</span>
                    </td>
                    <td className="py-3 pr-4">{renderActions(b)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <BookingsPagination
            page={page}
            totalPages={totalPages}
            total={total}
            pageSize={PAGE_SIZE}
            onPageChange={setPage}
            testid="all-pagination"
          />
        </>
      )}

      {otpFor && (
        <OtpDialog booking={otpFor.booking} action={otpFor.action} onVerified={handleVerified} onClose={() => setOtpFor(null)} />
      )}
      {editing && (
        <UserUpdateDialog booking={editing.booking} verifyToken={editing.token} onClose={() => setEditing(null)} onSaved={handleSaved} />
      )}
      {cancelling && (
        <CancelBookingDialog booking={cancelling.booking} onConfirm={handleCancelConfirm} onClose={() => setCancelling(null)} />
      )}
    </motion.section>
  );
};
